/**
 * Partner CRM API client (PR-5 — frontend для backend/app/api/partner_crm.py).
 *
 * Префикс `/partner-crm` (не `/partners` — занят ecosystemPartners.js,
 * recruiting agencies). См. ADR-010 + CLAUDE.md (Module Registration Checklist).
 */

import { apiFetch, apiFetchRaw } from './core'
import { partnerPlans } from './partnerPlans'
import { partnerBulkActions } from './partnerBulkActions'
import { partnerBulkAssign } from './partnerBulkAssign'
import { partnerTransfer } from './partnerTransfer'
import { partnerFunnels } from './partnerFunnels'

const BASE = '/partner-crm'

// ── Partners ──────────────────────────────────────────────────────────
export const partnerCrm = {
    listPartners: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/partners${suffix}`)
    },
    // Агрегатные счётчики доски по ВСЕЙ выборке под текущими фильтрами (не по
    // подгруженной странице). Питает ряд «Очередь» и KPI-ряд воронки.
    boardCounts: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/partners/counts${suffix}`)
    },
    // Значения для выпадающих фильтров «страна/город/источник» — свободный текст
    // в БД, поэтому список берём с сервера, а не даём вводить вслепую.
    filterValues: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/partners/filter-values${suffix}`)
    },
    getPartner: (id) => apiFetch(`${BASE}/partners/${id}`),
    // Дата последней сделки партнёра (PS2 закрытые ордера, иначе CRM) — для карточки.
    getPartnerLastDeal: (id) => apiFetch(`${BASE}/partners/${id}/last-deal`),

    // Живая финсводка партнёрки (оборот/маржа/активные + by_manager) из PS2.
    financeSummary: (params = {}) => {
        const qs = new URLSearchParams()
        if (params.managerId) qs.set('manager_id', params.managerId)
        if (params.period) qs.set('period', params.period)
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/finance/summary${suffix}`)
    },
    goalTree: (period = 'month') => apiFetch(`${BASE}/goal-tree?period=${encodeURIComponent(period)}`),
    companyGoalTree: (period = 'month') => apiFetch(`${BASE}/goal-tree/company?period=${encodeURIComponent(period)}`),
    setGoalNode: (nodeKey, body) => apiFetch(`${BASE}/goal-tree/node/${encodeURIComponent(nodeKey)}`, { method: 'PUT', body: JSON.stringify(body) }),
    createPartner: (data) =>
        apiFetch(`${BASE}/partners`, { method: 'POST', body: JSON.stringify(data) }),
    patchPartner: (id, data) =>
        apiFetch(`${BASE}/partners/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deletePartner: (id) =>
        apiFetch(`${BASE}/partners/${id}`, { method: 'DELETE' }),

    // ── Заявки на удаление (мягкое удаление с подтверждением руководителя) ──
    // Подать заявку на удаление карточки (любой с правом записи; менеджер — свою).
    requestDeletion: (id, reason) =>
        apiFetch(`${BASE}/partners/${id}/deletion-request`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
        }),
    // Очередь заявок для руководителя отдела / admin (status: PENDING|APPROVED|REJECTED).
    listDeletionRequests: (status) =>
        apiFetch(`${BASE}/deletion-requests${status ? `?status=${status}` : ''}`),
    approveDeletion: (requestId) =>
        apiFetch(`${BASE}/deletion-requests/${requestId}/approve`, { method: 'POST' }),
    rejectDeletion: (requestId, rejectionReason) =>
        apiFetch(`${BASE}/deletion-requests/${requestId}/reject`, {
            method: 'POST',
            body: JSON.stringify({ rejection_reason: rejectionReason }),
        }),
    // Восстановить мягко удалённую карточку (admin).
    restorePartner: (id) =>
        apiFetch(`${BASE}/partners/${id}/restore`, { method: 'POST' }),

    // ── Гео-лиды (инбокс бизнесов, спарсенных с карт) ──────────────────
    // Инбокс потенциальных партнёров из Google Maps / Яндекс Карт / 2ГИС.
    // Management-only (admin + head_of_partners) — backend форсирует роль.
    listGeoLeads: ({ status = 'new', source, city, hasWebsite, q, limit, offset } = {}) => {
        const qs = new URLSearchParams()
        const set = (k, v) => { if (v !== null && v !== undefined && v !== '') qs.set(k, String(v)) }
        set('status', status)
        set('source', source)
        set('city', city)
        set('has_website', hasWebsite)
        set('q', q)
        set('limit', limit)
        set('offset', offset)
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/geo-leads${suffix}`)
    },
    // Счётчики инбокса: total_new + by_source + by_status. Питает бейджи вкладок.
    geoLeadsCounts: () => apiFetch(`${BASE}/geo-leads/counts`),
    // Перенести выбранные лиды в воронку «Потенциальные партнёры» (lead_pool)
    // с назначением менеджера и сегмента. → { promoted, partner_ids, skipped_ids }.
    promoteGeoLeads: ({ leadIds, managerId, segment }) =>
        apiFetch(`${BASE}/geo-leads/promote`, {
            method: 'POST',
            body: JSON.stringify({
                lead_ids: leadIds,
                manager_id: managerId,
                segment: segment || null,
            }),
        }),
    // Отклонить выбранные лиды (с опциональной причиной). → { dismissed }.
    dismissGeoLeads: ({ leadIds, reason }) =>
        apiFetch(`${BASE}/geo-leads/dismiss`, {
            method: 'POST',
            body: JSON.stringify({ lead_ids: leadIds, reason: reason || null }),
        }),

    // Что предложить перевести после записи результата дела (или null).
    // Только предложение: карточку двигает менеджер, автопереходов нет.
    stageSuggestion: (id) => apiFetch(`${BASE}/partners/${id}/stage-suggestion`),

    transitionFunnel: (id, payload) =>
        apiFetch(`${BASE}/partners/${id}/funnel`, {
            method: 'POST',
            body: JSON.stringify(payload),
        }),
    // Воронки и их колонки — реализация в api/partnerFunnels.js (этот файл
    // упёрся в baseline размера).
    listFunnels: () => partnerFunnels.list(),
    createFunnel: (data) => partnerFunnels.create(data),
    patchFunnel: (funnelId, data) => partnerFunnels.patch(funnelId, data),
    deleteFunnel: (funnelId) => partnerFunnels.remove(funnelId),
    listFunnelStages: (funnelId) => partnerFunnels.listStages(funnelId),
    createFunnelStage: (data) => partnerFunnels.createStage(data),
    patchFunnelStage: (stageId, data) => partnerFunnels.patchStage(stageId, data),
    deleteFunnelStage: (stageId) => partnerFunnels.removeStage(stageId),
    reorderFunnelStages: (data) => partnerFunnels.reorderStages(data),
    listTransitions: (id, limit = 100) =>
        apiFetch(`${BASE}/partners/${id}/transitions?limit=${limit}`),
    // Пин даты этапа календарём на карточке (event_date=null — снять пин).
    setStageDate: (id, stage, eventDate) =>
        apiFetch(`${BASE}/partners/${id}/stage-date`, {
            method: 'POST',
            body: JSON.stringify({ stage, event_date: eventDate }),
        }),
    // Отметка «проверка пройдена» на этапе «На проверке» → зелёный светофор.
    setReviewPassed: (id, passed = true) =>
        apiFetch(`${BASE}/partners/${id}/review-passed`, {
            method: 'POST',
            body: JSON.stringify({ passed }),
        }),
    bulkFunnelTransition: ({ partnerIds, newStage, comment, rejectionReason }) =>
        apiFetch(`${BASE}/partners/bulk/funnel-transition`, {
            method: 'POST',
            body: JSON.stringify({
                partner_ids: partnerIds,
                new_stage: newStage,
                comment: comment || null,
                rejection_reason: rejectionReason || null,
            }),
        }),
    // Массовые действия — реализация в api/partnerBulkActions.js (этот файл
    // упёрся в baseline размера). Защита от повторной отправки — там же.
    bulkPlanTouch: (data) => partnerBulkActions.planTouch(data),
    bulkAssignManager: (data) => partnerBulkActions.assignManager(data),
    transferPartner: (id, opts) => partnerTransfer.transfer(id, opts),
    listEligibleManagers: () => partnerTransfer.eligibleManagers(),
    listReferrerOptions: (q) => apiFetch(`${BASE}/referrer-options?q=${encodeURIComponent(q)}`),
    getManagerHistory: (id, limit) => partnerTransfer.history(id, limit),
    linkHost: (id, hostPartnerId) =>
        apiFetch(`${BASE}/partners/${id}/link-host`, {
            method: 'POST',
            body: JSON.stringify({ host_partner_id: hostPartnerId }),
        }),

    // ── Недельный план касаний (доска недель, зеркало YouGile) ──────────
    touchPlanBoard: (period, managerId) => {
        const qs = new URLSearchParams({ period })
        if (managerId) qs.set('manager_id', managerId)
        return apiFetch(`${BASE}/touch-plans/board?${qs}`)
    },
    upsertTouchPlan: (data) =>
        apiFetch(`${BASE}/touch-plans`, { method: 'POST', body: JSON.stringify(data) }),
    patchTouchPlan: (id, data) =>
        apiFetch(`${BASE}/touch-plans/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteTouchPlan: (id) =>
        apiFetch(`${BASE}/touch-plans/${id}`, { method: 'DELETE' }),
    assignWeek: (data) =>
        apiFetch(`${BASE}/week-assignments`, { method: 'POST', body: JSON.stringify(data) }),
    unassignWeek: (id) =>
        apiFetch(`${BASE}/week-assignments/${id}`, { method: 'DELETE' }),

    // AI & Documents
    getAiAdvice: (partnerId) => apiFetch(`${BASE}/partners/${partnerId}/ai-advice`, { aiTimeout: true }),
    listDocuments: (partnerId) => apiFetch(`${BASE}/partners/${partnerId}/documents`),
    createDocument: (partnerId, data) => 
        apiFetch(`${BASE}/partners/${partnerId}/documents`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    getCeoDashboard: () => apiFetch(`${BASE}/ceo-dashboard`),

    // Library docs (global marketing materials & contract templates)
    listLibraryDocs: (category, docSubtype) => {
        const qs = new URLSearchParams()
        if (category) qs.set('category', category)
        if (docSubtype) qs.set('doc_subtype', docSubtype)
        const s = qs.toString()
        return apiFetch(`${BASE}/library/docs/${s ? `?${s}` : ''}`)
    },
    createLibraryDoc: (data) =>
        apiFetch(`${BASE}/library/docs/`, { method: 'POST', body: JSON.stringify(data) }),
    patchLibraryDoc: (id, data) =>
        apiFetch(`${BASE}/library/docs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteLibraryDoc: (id) =>
        apiFetch(`${BASE}/library/docs/${id}`, { method: 'DELETE' }),
    getDocVariables: (id) =>
        apiFetch(`${BASE}/library/docs/${id}/variables`),
    createDocFromTemplate: (data) =>
        apiFetch(`${BASE}/library/docs/from-template/`, { method: 'POST', body: JSON.stringify(data) }),
    exportDocPdf: (id) =>
        apiFetchRaw(`${BASE}/library/docs/${id}/export-pdf/`, { method: 'POST' }),
    sendDocEmail: (id, data) =>
        apiFetch(`${BASE}/library/docs/${id}/send-email/`, { method: 'POST', body: JSON.stringify(data) }),
    getDocStatus: (id) =>
        apiFetch(`${BASE}/library/docs/${id}/status/`),

    // Meetings
    listMeetings: (partnerId, params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/partners/${partnerId}/meetings${suffix}`)
    },
    createMeeting: (partnerId, data) =>
        apiFetch(`${BASE}/partners/${partnerId}/meetings`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    patchMeeting: (meetingId, data) =>
        apiFetch(`${BASE}/meetings/${meetingId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),
    // #1 «Шаг выполнен»: закрыть/переоткрыть следующий шаг встречи (гасит
    // напоминание/эскалацию/бейдж «шаг просрочен» без новой встречи).
    markMeetingNextActionDone: (meetingId, done = true) =>
        apiFetch(`${BASE}/meetings/${meetingId}/next-action-done`, {
            method: 'POST',
            body: JSON.stringify({ done }),
        }),

    // Org-wide journal (сквозной журнал встреч + dashboard для руководителя)
    listAllMeetings: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/meetings${suffix}`)
    },
    getMeetingsDashboard: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/meetings/dashboard-stats${suffix}`)
    },

    // Deals
    listDeals: (partnerId, status = null) => {
        const qs = status ? `?deal_status=${status}` : ''
        return apiFetch(`${BASE}/partners/${partnerId}/deals${qs}`)
    },
    createDeal: (partnerId, data) =>
        apiFetch(`${BASE}/partners/${partnerId}/deals`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    // Сделка из незапланированного факта PS2: сумму/валюту/дату сервер берёт
    // из самого ордера, здесь — только order_id и уточнения (продукт, заметка).
    createDealFromOrder: (partnerId, data) =>
        apiFetch(`${BASE}/partners/${partnerId}/deals/from-order`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    patchDeal: (dealId, data) =>
        apiFetch(`${BASE}/deals/${dealId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),

    // Finance summary (PR3) — оборот + техническая маржа по партнёрам за период.
    // partner_manager видит своих, full-scope роли — всех (опц. manager_id).
    getFinanceSummary: ({ period, managerId } = {}) => {
        const qs = new URLSearchParams()
        if (period) qs.set('period', period)
        if (managerId) qs.set('manager_id', managerId)
        const s = qs.toString()
        return apiFetch(`${BASE}/finance/summary${s ? `?${s}` : ''}`)
    },

    // Funnel daily stats (PR6) — ежедневная статистика воронки по менеджерам.
    // partner_manager видит своих, full-scope роли — всех (опц. managerId).
    getFunnelDaily: ({ dateFrom, dateTo, managerId } = {}) => {
        const qs = new URLSearchParams()
        if (dateFrom) qs.set('date_from', dateFrom)
        if (dateTo) qs.set('date_to', dateTo)
        if (managerId) qs.set('manager_id', managerId)
        const s = qs.toString()
        return apiFetch(`${BASE}/funnel/daily${s ? `?${s}` : ''}`)
    },
    // Создать касание (touch). status='planned' — плановое касание (стадия 1,
    // «горит» по сроку); без status — факт постфактум (можно задним числом).
    // idempotencyKey — защита от двойного создания дела. Планирование следующего
    // шага идёт вторым запросом после завершения: если ответ потеряется, менеджер
    // жмёт кнопку снова, и без ключа на сервере появляется пара одинаковых дел.
    // С ключом повторный запрос возвращает ИСХОДНЫЙ ответ, а не создаёт второе.
    createTouch: (payload, { idempotencyKey } = {}) =>
        apiFetch(`${BASE}/touches`, {
            method: 'POST',
            body: JSON.stringify(payload),
            ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
        }),
    // Заполнить факт по плановому касанию (стадия 2: plan → факт выполнен).
    completeTouch: (touchId, payload) =>
        apiFetch(`${BASE}/touches/${touchId}/complete`, {
            method: 'POST',
            body: JSON.stringify(payload),
        }),
    listTouches: ({ dateFrom, dateTo, managerId, partnerId, status } = {}) => {
        const qs = new URLSearchParams()
        if (dateFrom) qs.set('date_from', dateFrom)
        if (dateTo) qs.set('date_to', dateTo)
        if (managerId) qs.set('manager_id', managerId)
        if (partnerId) qs.set('partner_id', partnerId)
        if (status) qs.set('status', status)
        const s = qs.toString()
        return apiFetch(`${BASE}/touches${s ? `?${s}` : ''}`)
    },

    // PS2 host data
    getHostData: (partnerId) => apiFetch(`${BASE}/partners/${partnerId}/host-data`),
    searchHostDeal: (number, partnerId = null) => {
        const qs = new URLSearchParams({ number })
        if (partnerId) qs.set('partner_id', partnerId)
        return apiFetch(`${BASE}/search-host-deal?${qs}`)
    },

    // Product Catalog
    listProducts: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/products${suffix}`)
    },
    createProduct: (data) =>
        apiFetch(`${BASE}/products`, { method: 'POST', body: JSON.stringify(data) }),
    patchProduct: (id, data) =>
        apiFetch(`${BASE}/products/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteProduct: (id) => apiFetch(`${BASE}/products/${id}`, { method: 'DELETE' }),

    // Мероприятия как канал привлечения (п. 15 инструкции ОПП)
    listEvents: (q = '') => apiFetch(`${BASE}/events${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    createEvent: (data) => apiFetch(`${BASE}/events`, { method: 'POST', body: JSON.stringify(data) }),

    // Partner ↔ Product M2M
    listPartnerProducts: (partnerId) => apiFetch(`${BASE}/partners/${partnerId}/products`),
    addPartnerProduct: (partnerId, data) =>
        apiFetch(`${BASE}/partners/${partnerId}/products`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    patchPartnerProduct: (linkId, data) =>
        apiFetch(`${BASE}/partner-products/${linkId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),
    deletePartnerProduct: (linkId) =>
        apiFetch(`${BASE}/partner-products/${linkId}`, { method: 'DELETE' }),

    // Plans — реализация в api/partnerPlans.js (этот файл упёрся в baseline
    // размера); здесь тонкие делегаты, чтобы не ломать существующих потребителей.
    listPartnerPlans: (partnerId) => partnerPlans.listForPartner(partnerId),
    listPlansForPeriod: (period) => partnerPlans.listForPeriod(period),
    planSummary: (period, managerId) => partnerPlans.summary(period, managerId),
    planGap: (period, managerId, months) => partnerPlans.gap(period, managerId, months),
    partnerFinancials: (partnerIds, period, months) => partnerPlans.financials(partnerIds, period, months),
    createPlan: (data) => partnerPlans.create(data),
    patchPlan: (id, data) => partnerPlans.patch(id, data),
    deletePlan: (id) => partnerPlans.remove(id),

    // Per-partner statistics (deals/meetings/referral/PS2)
    getPartnerStatistics: (partnerId) => apiFetch(`${BASE}/partners/${partnerId}/statistics`),

    // Analytics Suite (ADR-013)
    getAnalyticsOverview: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/analytics/overview${suffix}`)
    },
    // Drill-down «Сделок сети»: список закрытых сделок сети менеджера за период.
    getNetworkDeals: ({ managerFi, period, dateFrom, dateTo, scope } = {}) => {
        const qs = new URLSearchParams()
        if (managerFi) qs.set('manager_fi', managerFi)
        // Период обязателен: без него бэкенд берёт свой умолчательный «месяц», и
        // окно показывает месяц, тогда как число в колонке посчитано за всё время.
        if (period) qs.set('period', period)
        if (dateFrom) qs.set('date_from', dateFrom)
        if (dateTo) qs.set('date_to', dateTo)
        if (scope) qs.set('scope', scope)
        const s = qs.toString()
        return apiFetch(`${BASE}/analytics/network-deals${s ? `?${s}` : ''}`)
    },
    // Drill-down «Без карточки в CRM»: партнёры сети менеджера без карточки (тот же
    // набор и то же окно, что у счётчика partners_without_card) — карточка в 1 клик.
    getNetworkPartnersWithoutCard: ({ managerFi, period, dateFrom, dateTo } = {}) => {
        const qs = new URLSearchParams()
        if (managerFi) qs.set('manager_fi', managerFi)
        if (period) qs.set('period', period)
        if (dateFrom) qs.set('date_from', dateFrom)
        if (dateTo) qs.set('date_to', dateTo)
        const s = qs.toString()
        return apiFetch(`${BASE}/analytics/network-partners-without-card${s ? `?${s}` : ''}`)
    },
    getAnalyticsPersonal: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/analytics/personal${suffix}`)
    },
    // «Я и команда» — обезличенное сравнение менеджера с командой отдела.
    getAnalyticsTeamComparison: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== null && v !== undefined && v !== '') qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/analytics/team-comparison${suffix}`)
    },
    // Расширенная аналитика воронки (conversion_rates между этапами, avg time,
    // weekly_trend). Full-scope роли; бэкенд get_funnel_metrics. Улучшение #6.
    getFunnelMetrics: () => apiFetch(`${BASE}/analytics/funnel-metrics`),

    // Activity Stream (ADR-013) — unified timeline meetings + deals + audit + plans
    getPartnerActivity: (partnerId, limit = 50) =>
        apiFetch(`${BASE}/partners/${partnerId}/activity?limit=${limit}`),

    // Единый блок «Дела» (PR-A): to_do / done / next / refused + контакты партнёра.
    getPartnerActions: (partnerId) => apiFetch(`${BASE}/partners/${partnerId}/actions`),

    // Cross-search (партнёры + сделки одним запросом)
    crossSearch: (q) => apiFetch(`${BASE}/search?q=${encodeURIComponent(q || '')}`),

    // SLA Admin (Phase 6 PR-F)
    listSlaRules: () => apiFetch(`${BASE}/sla/rules`),
    createSlaRule: (data) =>
        apiFetch(`${BASE}/sla/rules`, { method: 'POST', body: JSON.stringify(data) }),
    patchSlaRule: (id, data) =>
        apiFetch(`${BASE}/sla/rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteSlaRule: (id) =>
        apiFetch(`${BASE}/sla/rules/${id}`, { method: 'DELETE' }),
    setSlaTimezone: (timezone) =>
        apiFetch(`${BASE}/sla/timezone`, { method: 'PUT', body: JSON.stringify({ timezone }) }),

    // Segment Thresholds Admin (USD-only после Senate-style refactor)
    getSegmentThresholds: () => apiFetch(`${BASE}/segments/thresholds`),
    getSegmentThresholdsConverted: (ref = 'USD') =>
        apiFetch(`${BASE}/segments/thresholds/converted?to=${encodeURIComponent(ref)}`),
    setSegmentThresholds: (items) =>
        apiFetch(`${BASE}/segments/thresholds`, { method: 'PUT', body: JSON.stringify({ items }) }),
    resetSegmentThresholds: () =>
        apiFetch(`${BASE}/segments/thresholds`, { method: 'DELETE' }),

    // FX rates (CCY → RUB) для formatRevenueDual «X CCY / Y USD».
    getFxRates: () => apiFetch(`${BASE}/fx/rates`),

    // Health Score (ADR-013) — composite 0..100 + breakdown + recommendations
    getPartnerHealth: (partnerId) => apiFetch(`${BASE}/partners/${partnerId}/health`),

    // Admin: tunable Health Score weights
    getAnalyticsConfig: () => apiFetch(`${BASE}/admin/analytics/config`),
    patchAnalyticsConfig: (weights) =>
        apiFetch(`${BASE}/admin/analytics/config`, {
            method: 'PATCH',
            body: JSON.stringify({ health_score_weights: weights }),
        }),
    getAttributionAudit: ({ fi = 'fi1073', months = 12 } = {}) => {
        const qs = new URLSearchParams()
        qs.set('fi', fi)
        qs.set('months', String(months))
        return apiFetch(`${BASE}/admin/attribution-audit?${qs.toString()}`)
    },

    // Bulk-привязка — реализация в api/partnerBulkAssign.js (этот файл на
    // потолке гейта размера). Менеджер и его fi-код приезжают ответом сервера.
    adminManagerCandidates: () => partnerBulkAssign.managerCandidates(),
    adminAssignPartnersToManager: (managerId, refCode, apply) => partnerBulkAssign.assign(managerId, refCode, apply),

    // ADMIN: wipe всех партнёров (после ошибочного импорта)
    adminWipePartners: () =>
        apiFetch(`${BASE}/admin/wipe?confirm=YES_DELETE_ALL`, { method: 'DELETE' }),

    // ADMIN: bulk import всех партнёров из PS2 (Senate Operations)
    importFromPs2: (limit = 5000) =>
        apiFetch(`${BASE}/import-from-ps2?limit=${limit}`, { method: 'POST' }),

    // CSV Import (bulk upsert по ref_code primary, partner_number fallback)
    getImportSupportedFields: () => apiFetch(`${BASE}/import/supported-fields`),
    importPreview: (file, mapping, delimiter = ',') => {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('column_mapping', JSON.stringify(mapping))
        fd.append('delimiter', delimiter)
        return apiFetch(`${BASE}/import/preview`, { method: 'POST', body: fd })
    },
    importCommit: (file, mapping, delimiter = ',') => {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('column_mapping', JSON.stringify(mapping))
        fd.append('delimiter', delimiter)
        return apiFetch(`${BASE}/import/commit`, { method: 'POST', body: fd })
    },

    // Referral tree + Analytics
    // A6: партнёры PS2, подключённые (есть начисления), но без карточки в CRM.
    unlinkedConnected: () => apiFetch(`${BASE}/analytics/unlinked-connected`),
    getReferralTree: (partnerId, maxDepth = 10) =>
        apiFetch(`${BASE}/partners/${partnerId}/referral-tree?max_depth=${maxDepth}`),
    // Ветка менеджера: к какому менеджеру относится партнёр + метрики ширина/глубина.
    getPartnerBranch: (partnerId) => apiFetch(`${BASE}/partners/${partnerId}/branch`),
    getManagerBranches: () => apiFetch(`${BASE}/managers/branches`),
    // Дерево с PS2-метриками на каждом узле (оборот/маржа/вознаграждение L1/L2).
    getManagerTree: (managerId, { from, to } = {}) => {
        const qs = new URLSearchParams()
        if (from) qs.set('from', from)
        if (to) qs.set('to', to)
        const q = qs.toString()
        return apiFetch(`${BASE}/managers/${managerId}/tree${q ? '?' + q : ''}`)
    },
    getManagerNextActions: (managerId) => apiFetch(`${BASE}/managers/${managerId}/next-actions`),
    // Визуальное дерево сети менеджера из PS2 (реальные оборот/сделки/заработок на
    // узле). fi менеджера резолвится на бэке из Employee.partner_code_fi; руководство
    // может смотреть чужой через ?fi=. См. постмортем 2026-07-14.
    getNetworkTree: ({ fi } = {}) => apiFetch(`${BASE}/analytics/network-tree${fi ? `?fi=${encodeURIComponent(fi)}` : ''}`),
    // Объём/маржа по периодам (week/month/quarter/year) + MTD + накопительный итог.
    getPartnerVolume: (partnerId, { granularity = 'month', from, to } = {}) => {
        const qs = new URLSearchParams({ granularity })
        if (from) qs.set('from', from)
        if (to) qs.set('to', to)
        return apiFetch(`${BASE}/partners/${partnerId}/volume?${qs.toString()}`)
    },
    getManagerVolume: (managerId, { granularity = 'month', from, to } = {}) => {
        const qs = new URLSearchParams({ granularity })
        if (from) qs.set('from', from)
        if (to) qs.set('to', to)
        return apiFetch(`${BASE}/managers/${managerId}/volume?${qs.toString()}`)
    },
    getAnalytics: (params = {}) => {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v) qs.set(k, String(v))
        }
        const suffix = qs.toString() ? `?${qs}` : ''
        return apiFetch(`${BASE}/analytics${suffix}`)
    },

    // Tasks (Task Board)
    listTasks: (partnerId, limit = 100) =>
        apiFetch(`${BASE}/partners/${partnerId}/tasks?limit=${limit}`),
    createTask: (partnerId, data) =>
        apiFetch(`${BASE}/partners/${partnerId}/tasks`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    patchTask: (taskId, data) =>
        apiFetch(`${BASE}/tasks/${taskId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),
    // Кросс-партнёрский список задач (страница «Дела») с фильтрами.
    listAllTasks: ({ managerId, status, dueFrom, dueTo, limit } = {}) => {
        const qs = new URLSearchParams()
        if (managerId) qs.set('manager_id', managerId)
        if (status) qs.set('status', status)
        if (dueFrom) qs.set('due_from', dueFrom)
        if (dueTo) qs.set('due_to', dueTo)
        if (limit) qs.set('limit', String(limit))
        const s = qs.toString()
        return apiFetch(`${BASE}/tasks${s ? `?${s}` : ''}`)
    },

    // Comments
    listComments: (partnerId, limit = 100) =>
        apiFetch(`${BASE}/partners/${partnerId}/comments?limit=${limit}`),
    createComment: (partnerId, data) =>
        apiFetch(`${BASE}/partners/${partnerId}/comments`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    // ── Executive (собственник партнёрки) — стратегический слой ───────
    // BASE='/partner-crm' /exec/*: overview/trend/funnel-conversion/velocity.
    // Без операционного шума (имён менеджеров, касаний) — это слой собственника.
    getExecOverview: ({ period } = {}) => {
        const qs = new URLSearchParams()
        if (period) qs.set('period', period)
        const s = qs.toString()
        return apiFetch(`${BASE}/exec/overview${s ? `?${s}` : ''}`)
    },
    getExecTrend: ({ months = 12 } = {}) =>
        apiFetch(`${BASE}/exec/trend?months=${months}`),
    getExecFunnelConversion: () => apiFetch(`${BASE}/exec/funnel-conversion`),
    getExecVelocity: () => apiFetch(`${BASE}/exec/velocity`),
}

export const PARTNER_PRODUCT_STATUS_LABELS = {
    potential: 'Потенциал',
    active: 'Активен',
    paused: 'На паузе',
    dropped: 'Отказ',
}

export const PARTNER_PRODUCT_STATUS_COLORS = {
    potential: 'var(--color-text-muted)',
    active: 'var(--color-success)',
    paused: 'var(--color-warning)',
    dropped: 'var(--color-danger)',
}

// ── Label maps (зеркало backend enum'ов из app/schemas/partners.py) ───

export const FUNNEL_STAGE_LABELS = {
    lead_pool: 'Потенциальные',
    lead: 'Лид',  // deprecated 2026-07-14: слит в «Потенциальные», ярлык для истории
    first_contact: 'Первый контакт',
    kp_sent: 'КП отправлено',
    application_submitted: 'Заявка отправлена',  // deprecated: слит в «КП отправлено»
    under_review_3d: 'На проверке',  // deprecated: слит в «КП отправлено»
    approved: 'Подключён',
    rejected: 'Отказ',
    onboarding: 'Онбординг',
    active: 'Активен',
    developing: 'Развитие',
    scaling: 'Масштабирование',
    at_risk: 'Под риском',
    churned: 'Отток',
}

export const FUNNEL_STAGE_COLORS = {
    lead_pool: 'var(--color-text-muted)',
    lead: 'var(--color-text-muted)',
    first_contact: 'var(--color-text-secondary)',
    kp_sent: 'var(--color-pipeline-offer)',
    application_submitted: 'var(--color-info)',
    under_review_3d: 'var(--color-warning)',
    approved: 'var(--color-success)',
    rejected: 'var(--color-danger)',
    onboarding: 'var(--color-purple)',
    active: 'var(--color-success)',
    developing: 'var(--color-success)',
    scaling: 'var(--color-pipeline-offer)',
    at_risk: 'var(--color-warning)',
    churned: 'var(--color-text-muted)',
}

// Две воронки: Привлечение (Acquisition) и Развитие (Development).
// `approved` — мост: финал Acquisition, старт Development (отображается в обеих).
export const ACQUISITION_STAGES = [
    'lead_pool', 'first_contact', 'kp_sent', 'approved', 'rejected',
]
export const DEVELOPMENT_STAGES = [
    'onboarding', 'active', 'developing',
    'scaling', 'at_risk', 'churned',
]

// Стадии, выведенные из воронки 2026-07-14 (слиты в lead_pool/kp_sent). Ярлыки в
// FUNNEL_STAGE_LABELS оставлены для истории переходов, но в дропдаунах ВЫБОРА
// (фильтр «Все этапы», bulk-перенос) их показывать не нужно — карточек там 0.
export const DEPRECATED_FUNNEL_STAGES = new Set(['lead', 'application_submitted', 'under_review_3d'])

export const SEGMENT_LABELS = {
    small: 'Малый',
    medium: 'Средний',
    key: 'Ключевой',
    strategic: 'Стратегический',
}

// Источники гео-лидов (спарсенные с карт бизнесы) — код → человекочитаемый ярлык.
export const GEO_LEAD_SOURCE_LABELS = {
    google_maps: 'Google Maps',
    yandex_maps: 'Яндекс Карты',
    '2gis': '2ГИС',
}

// Тон бейджа источника для DataTable (Badge tones — не hex, DS-совместимо).
export const GEO_LEAD_SOURCE_TONE = {
    google_maps: 'info',
    yandex_maps: 'danger',
    '2gis': 'success',
}

// Phase 5 — критерии сегментов для tooltip (черновые цифры по фрейму
// ADR-8 §13). Финальные пороги — через admin UI отдельным PR.
export const SEGMENT_CRITERIA = {
    small: {
        label: 'Малый',
        criteria: 'Оборот < 1 млн ₽/мес · ≤ 1 точка · потенциал низкий',
        response_sla: 'Срок реакции: до 7 дней',
    },
    medium: {
        label: 'Средний',
        criteria: 'Оборот 1-3 млн ₽/мес · 2-3 точки · потенциал средний',
        response_sla: 'Срок реакции: 2-3 дня',
    },
    key: {
        label: 'Ключевой',
        criteria: 'Оборот 3-10 млн ₽/мес · 3-5 точек · потенциал высокий',
        response_sla: 'Срок реакции: до 1 дня',
    },
    strategic: {
        label: 'Стратегический',
        criteria: 'Оборот > 10 млн ₽/мес · 5+ точек · потенциал стратегический',
        response_sla: 'Срок реакции: 1 день',
    },
}

// ── Touch (касание) enum labels (PR6) — зеркало backend enum'ов ───────

export const TOUCH_TYPE_LABELS = {
    call: 'Звонок',
    message: 'Сообщение',
    meeting: 'Встреча',
    visit: 'Визит',
    other: 'Другое',
}

export const TOUCH_RESULT_LABELS = {
    connected: 'Подключение',
    negotiating: 'Переговоры',
    scheduled: 'Назначена встреча',
    no_answer: 'Нет ответа',
    refused: 'Отказ',
    other: 'Другое',
}

export const TOUCH_RESULT_COLORS = {
    connected: 'var(--color-success)',
    negotiating: 'var(--color-info)',
    scheduled: 'var(--color-purple)',
    no_answer: 'var(--color-text-muted)',
    refused: 'var(--color-danger)',
    other: 'var(--color-text-muted)',
}

export const REJECTION_REASON_LABELS = {
    no_need: 'Нет потребности',
    conditions: 'Условия/комиссия',
    competitor: 'Конкурент',
    no_trust: 'Нет доверия',
    no_response: 'Не выходит на связь',
    other: 'Другое',
}

// Причины отказа на доске воронки (переход в «Отказ») — планёрка ОПП 2026-07-14.
// Отдельный набор из 4 (не путать с REJECTION_REASON_LABELS касаний).
export const FUNNEL_REJECTION_REASON_LABELS = {
    competitor: 'Конкуренты',
    not_interested: 'Не интересно',
    ignored: 'Игнор',
    hard_refusal: 'Жёсткий отказ',
}

export const REJECTION_REASON_COLORS = {
    no_need: 'var(--color-text-muted)',
    conditions: 'var(--color-warning)',
    competitor: 'var(--color-info)',
    no_trust: 'var(--color-purple)',
    no_response: 'var(--color-text-secondary)',
    other: 'var(--color-text-muted)',
}

export const TOUCH_LOCATION_LABELS = {
    office_partner: 'Офис партнёра',
    our_office: 'Наш офис',
    coworking: 'Коворкинг',
    online: 'Онлайн',
    other: 'Другое',
}

export const DEAL_STATUS_LABELS = {
    planned: 'Запланирована',
    in_progress: 'В работе',
    passed_manual: 'Прошла (вручную)',
    closed: 'Закрыта',
    cancelled: 'Отменена',
}

// Нормализованные категории индустрий (зеркало backend IndustryCategory enum).
// Free-text industry классифицируется backend'ом через
// IndustryClassificationGate.classify(). Иконки — Lucide-компоненты в потребителе
// (см. INDUSTRY_ICONS в PartnersFunnelView.jsx); цветные emoji запрещены (DS).
export const INDUSTRY_CATEGORY_LABELS = {
    real_estate: 'Недвижимость',
    tourism: 'Туризм',
    currency_exchange: 'Обмен валют',
    consulting: 'Консалтинг',
    vehicle_rental: 'Аренда транспорта',
    health_beauty: 'Здоровье и красота',
    other: 'Другое',
}

export const SEGMENT_DOTS = {
    small: { label: 'S', color: 'var(--color-text-muted)' },
    medium: { label: 'M', color: 'var(--color-info)' },
    key: { label: 'K', color: 'var(--color-success)' },
    strategic: { label: 'St', color: 'var(--color-purple)' },
}

export const PARTNER_TASK_STATUS_LABELS = {
    todo: 'К выполнению',
    in_progress: 'В работе',
    done: 'Готово',
}

export const PARTNER_TASK_STATUS_COLORS = {
    todo: 'var(--color-text-muted)',
    in_progress: 'var(--color-info)',
    done: 'var(--color-success)',
}

export const PARTNER_TASK_PRIORITY_LABELS = {
    low: 'Низкий',
    medium: 'Средний',
    high: 'Высокий',
}

export const PARTNER_TASK_PRIORITY_COLORS = {
    low: 'var(--color-success)',
    medium: 'var(--color-warning)',
    high: 'var(--color-danger)',
}
